import React from "react";
import { Box, Text, VStack, HStack, Progress, Badge } from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";

const MotionProgress = motion(Progress);

export default function SubscriptionStatus() {
  const { user, loading } = useAuth();

  if (loading) return <Text fontSize="sm">Loading...</Text>;
  if (!user) return null;

  const planName = user.subscription?.plan || "free";
  const isPro = planName === "pro";
  const isFree = planName === "free";

  const watchlistCount = user.watchlist?.length || 0;
  const watchlistLimit =
    user.limits?.watchlist || (isPro ? Infinity : planName === "starter" ? 20 : 3);

  const aiUsed = user.aiQueriesUsed || 0;
  const aiLimit = user.limits?.aiQueries || (planName === "free" ? 5 : 100);

  const watchlistPercent = Math.min((watchlistCount / watchlistLimit) * 100, 100);
  const aiQueriesPercent = Math.min((aiUsed / aiLimit) * 100, 100);

  const planColor = isPro ? "purple" : planName === "starter" ? "blue" : "orange";

  const periodEnd = user.subscription?.currentPeriodEnd
    ? new Date(user.subscription.currentPeriodEnd)
    : null;
  const isCanceled = user.subscription?.cancelAtPeriodEnd;

  return (
    <Box
      p={3}
      rounded="lg"
      shadow="sm"
      bg="transparent"
      borderWidth="1px"
      borderColor="gray.200"
      maxW="250px"
    >
      <VStack spacing={2} align="stretch">
        {/* Plan Display */}
        <Box>
          <HStack spacing={2}>
            <Text fontSize="sm" fontWeight="bold">
              Plan:
            </Text>
            <Badge
              bg={`${planColor}.500`}
              color="white"
              px={2}
              py={1}
              fontSize="sm"
              fontWeight="bold"
              borderRadius="md"
            >
              {planName.toUpperCase()}
            </Badge>
          </HStack>
          {!isFree && periodEnd && (
            <Text fontSize="xs" color="gray.500" mt={0.5}>
              {isCanceled
                ? `Expires on ${periodEnd.toLocaleDateString()}`
                : `Renew on ${periodEnd.toLocaleDateString()}`}
            </Text>
          )}
        </Box>

        {/* Only show Watchlist bar if not Pro */}
        {!isPro && (
          <Box>
            <HStack justify="space-between" mb={1}>
              <Text fontSize="xs">Watchlist</Text>
              <Text fontSize="xs" fontWeight="bold">
                {watchlistCount}/{watchlistLimit}
              </Text>
            </HStack>
            <AnimatePresence>
              <MotionProgress
                value={watchlistPercent}
                colorScheme={planColor}
                size="xs"
                borderRadius="md"
                initial={{ width: 0 }}
                animate={{ width: `${watchlistPercent}%` }}
                transition={{ duration: 0.5 }}
              />
            </AnimatePresence>
          </Box>
        )}

        {/* Ask AI bar */}
        <Box>
          <HStack justify="space-between" mb={1}>
            <Text fontSize="xs">Ask AI</Text>
            <Text fontSize="xs" fontWeight="bold">
              {aiUsed}/{aiLimit}
            </Text>
          </HStack>
          <AnimatePresence>
            <MotionProgress
              value={aiQueriesPercent}
              colorScheme={planColor}
              size="xs"
              borderRadius="md"
              initial={{ width: 0 }}
              animate={{ width: `${aiQueriesPercent}%` }}
              transition={{ duration: 0.5 }}
            />
          </AnimatePresence>
        </Box>
      </VStack>
    </Box>
  );
}
